import { useEffect, useRef } from 'react'

import { useCoAgent } from '@copilotkit/react-core'
import { useAiChat, useF0AiFormRegistry } from '@factorialco/f0-react'

interface CoAgentFormState {
  activeForm?: {
    formName?: string
    defaultValuesParams?: Record<string, unknown>
  } | null
  formsOnCurrentPage?: unknown
  availableForms?: unknown
}

/**
 * Plumbing only — not a CopilotKit action. Mirrors the active form / forms-on-
 * page state from the local `F0AiFormRegistry` into the Mastra co-agent shared
 * state, and pulls back any `defaultValuesParams` Mastra writes so the form
 * registry can rebuild descriptions without losing them.
 *
 * Must be called inside a tree with a `F0AiFormRegistryProvider` ancestor and
 * a CopilotKit context.
 */
export const useFormCoAgentSync = () => {
  const registry = useF0AiFormRegistry()

  const { agent: agentName } = useAiChat()

  const { setState, state } = useCoAgent<CoAgentFormState>({
    name: agentName || 'one-workflow',
  })

  // Sync defaultValuesParams from Mastra shared state → registry entry so
  // rebuildDescriptions emits them without needing a preservation workaround.
  const prevParamsKeyRef = useRef('')
  useEffect(() => {
    if (!registry) return
    const formName = state?.activeForm?.formName
    const params = state?.activeForm?.defaultValuesParams
    if (!formName) return
    const paramsKey = `${formName}:${JSON.stringify(params)}`
    if (prevParamsKeyRef.current === paramsKey) return
    prevParamsKeyRef.current = paramsKey
    registry.updateActiveFormDefaultValuesParams(formName, params)
  }, [
    registry,
    state?.activeForm?.formName,
    JSON.stringify(state?.activeForm?.defaultValuesParams),
  ])

  useEffect(() => {
    // Write `formsOnCurrentPage` / `availableForms` / `activeForm` into the
    // coagent shared state EAGERLY on mount and on every registry change —
    // not gated by `running`. The agent receives state at the moment of the
    // first message; if we wait for the agent to start running, the first
    // message arrives with these fields missing and the agent (legitimately)
    // responds "the editor is syncing".
    if (!registry) return

    const formsOnCurrentPage = registry.formsOnCurrentPage || []
    const availableForms = registry.availableForms || []
    const activeForm = registry.activeForm || null

    setState((currentState) => {
      // Preserve defaultValuesParams that Mastra wrote into the coagent state.
      // The registry entry stores them but they may not appear in the rebuilt
      // description yet (updateActiveFormDefaultValuesParams skips rebuild to
      // avoid a re-render cascade). Carry them forward until a natural rebuild
      // (e.g. fillForm) includes them.
      const prevActiveForm = currentState?.activeForm
      const mergedActiveForm =
        activeForm && prevActiveForm?.defaultValuesParams && !activeForm.defaultValuesParams
          ? {
              ...activeForm,
              defaultValuesParams: prevActiveForm.defaultValuesParams,
            }
          : activeForm

      return {
        ...(currentState ?? {}),
        formsOnCurrentPage,
        availableForms,
        activeForm: mergedActiveForm,
      }
    })
  }, [
    JSON.stringify(registry?.formsOnCurrentPage),
    JSON.stringify(registry?.availableForms),
    JSON.stringify(registry?.activeForm),
  ])
}
